import SearchIcon from '@mui/icons-material/Search';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Input from '@mui/material/Input';
import InputAdornment from '@mui/material/InputAdornment';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useState } from 'react';

import type { VerbConjugation, VerbForms } from '@/api/types';
import { registerLocaleResources, useTranslation } from '@/modules/i18n/useTranslation';

import VerbCell from '../VerbCell';
import { PRONOUNS } from '../verbs';
import en from './locales/en.json';
import es from './locales/es.json';
import ru from './locales/ru.json';
import { styles } from './style';
import WordBuffer from './WordBuffer';

registerLocaleResources('VerbTable', { en, es, ru });

declare module '@/modules/i18n/namespaces' {
    interface LocaleNamespaces {
        VerbTable: typeof en;
    }
}

interface VerbTableProps {
    conjugation: VerbConjugation;
    words: readonly string[];
    onChangeVerb: (verb: string) => void;
}

interface Section {
    label: string;
    cells: { forms: VerbForms; titles: string[] }[];
}

// "Past simple" covers both pretérito imperfecto and pretérito perfecto simple
const getPastSimple = ({ preterito_imperfecto, preterito_perfecto_simple }: VerbConjugation): VerbForms => {
    const pastSimple: VerbForms = {};
    for (const [pronoun] of PRONOUNS) {
        const form = preterito_imperfecto[pronoun];
        if (form !== undefined) {
            pastSimple[pronoun] = `${form} | ${preterito_perfecto_simple[pronoun] ?? ''}`;
        }
    }
    return pastSimple;
};

// Spanish tenses projected onto the English tense grid; continuous tenses have no projection yet.
// The grid is the subject of the page, so its English tense names and examples stay English in every language
const getSections = (conjugation: VerbConjugation): Section[] => [
    {
        label: 'Simple',
        cells: [
            { forms: getPastSimple(conjugation), titles: ['I did something'] },
            { forms: conjugation.presente, titles: ['I do something'] },
            { forms: conjugation.futuro_imperfecto, titles: ['I will do something'] },
        ],
    },
    {
        label: 'Perfect',
        cells: [
            { forms: conjugation.pluscuamperfecto, titles: ['I had done something'] },
            { forms: conjugation.preterito_perfecto, titles: ['I have done something'] },
            { forms: conjugation.futuro_perfecto, titles: ['I will have done something'] },
        ],
    },
    {
        label: 'Continuous',
        cells: [
            { forms: {}, titles: ['I was doing something'] },
            { forms: {}, titles: ['I am doing something'] },
            { forms: {}, titles: ['I will be doing something'] },
        ],
    },
    {
        label: 'Perfect continuous',
        cells: [
            { forms: {}, titles: ['I had been doing something'] },
            { forms: {}, titles: ['I have been doing something'] },
            { forms: {}, titles: ['I will have been doing something'] },
        ],
    },
];

const VerbTable = ({ conjugation, words, onChangeVerb }: VerbTableProps) => {
    const { t } = useTranslation('VerbTable');
    const [searchText, setSearchText] = useState('');
    const search = () => onChangeVerb(searchText);

    return (
        <div>
            <br/>
            <Box sx={styles.textFrequentlyUsedTitle}>{t('frequentlyUsed')}</Box>
            <br/>
            <WordBuffer words={words} selectedWord={conjugation.infinitive_es} onSelect={onChangeVerb}/>
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>
                            <Input
                                endAdornment={
                                    <InputAdornment position="start">
                                        <IconButton aria-label={t('search')} sx={styles.searchButton} onClick={search}>
                                            <SearchIcon/>
                                        </IconButton>
                                    </InputAdornment>
                                }
                                title={t('search')}
                                value={searchText}
                                placeholder={t('searchPlaceholder')}
                                onChange={(event) => setSearchText(event.target.value)}
                                onKeyDown={(event) => {
                                    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                                        search();
                                    }
                                }}
                            />
                            <div>{t('infinitiveEn')} {conjugation.infinitive_en}</div>
                            <div>{t('infinitiveRu')} {conjugation.infinitive_ru}</div>
                            <div>{t('infinitiveEs')} {conjugation.infinitive_es}</div>
                            <div>Gerundio: {conjugation.gerundio}</div>
                            <div>Participio: {conjugation.participio}</div>
                        </TableCell>
                    </TableRow>
                    <TableRow>
                        <TableCell>Past</TableCell>
                        <TableCell>Present</TableCell>
                        <TableCell>Future</TableCell>
                    </TableRow>
                </TableHead>
                {getSections(conjugation).map(({ label, cells }) => (
                    <TableBody key={label}>
                        <TableRow>
                            <TableCell component="th" scope="rowgroup" variant="head">{label}</TableCell>
                        </TableRow>
                        <TableRow>
                            {cells.map(({ forms, titles }) => (
                                <VerbCell key={titles[0]} forms={forms} titles={titles}/>
                            ))}
                        </TableRow>
                    </TableBody>
                ))}
            </Table>
        </div>
    );
};

export default VerbTable;
